import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { formatNumber } from '@/common/lib/utils'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import type { ColumnDef } from '@tanstack/react-table'
import type { QuotaPurchaseHistoryItem } from '../../types'
import { QUOTA_HISTORY_TABLE_COLUMNS } from './constants'
import { Badge } from '@/common/ui/badge'

type Props = {
	data: QuotaPurchaseHistoryItem[]
	totalItems: number
	isLoading: boolean
}

const columns: ColumnDef<QuotaPurchaseHistoryItem>[] = [
	{
		accessorKey: QUOTA_HISTORY_TABLE_COLUMNS[0].key,
		header: QUOTA_HISTORY_TABLE_COLUMNS[0].label,
		cell: ({ row }) => {
			const date = new Date(row.original.createdAt)
			return (
				<div className="space-y-0.5">
					<div className="text-sm font-medium">
						{format(date, 'dd MMM yyyy', { locale: th })}
					</div>
					<div className="text-xs text-muted-foreground">
						{format(date, 'HH:mm:ss')}
					</div>
				</div>
			)
		},
	},
	{
		accessorKey: QUOTA_HISTORY_TABLE_COLUMNS[1].key,
		header: QUOTA_HISTORY_TABLE_COLUMNS[1].label,
		cell: ({ row }) => (
			<div className="space-y-0.5">
				<div className="font-semibold text-green-600">
					{formatNumber(row.original.winningQuotaAmount)}
				</div>
				{row.original.winningQuotaFromAmount !== undefined && (
					<div className="text-xs text-muted-foreground">
						{formatNumber(row.original.winningQuotaFromAmount)} →{' '}
						{formatNumber(row.original.winningQuotaToAmount ?? 0)}
					</div>
				)}
			</div>
		),
	},
	{
		accessorKey: QUOTA_HISTORY_TABLE_COLUMNS[2].key,
		header: QUOTA_HISTORY_TABLE_COLUMNS[2].label,
		cell: ({ row }) => (
			<div className="space-y-0.5">
				<div className="font-semibold text-blue-600">
					{formatNumber(row.original.creditQuotaAmount)}
				</div>
				{row.original.creditQuotaFromAmount !== undefined && (
					<div className="text-xs text-muted-foreground">
						{formatNumber(row.original.creditQuotaFromAmount)} →{' '}
						{formatNumber(row.original.creditQuotaToAmount ?? 0)}
					</div>
				)}
			</div>
		),
	},
	{
		accessorKey: QUOTA_HISTORY_TABLE_COLUMNS[3].key,
		header: QUOTA_HISTORY_TABLE_COLUMNS[3].label,
		cell: ({ row }) => (
			<div className="font-semibold">{formatNumber(row.original.amount)}</div>
		),
	},
	{
		accessorKey: QUOTA_HISTORY_TABLE_COLUMNS[4].key,
		header: QUOTA_HISTORY_TABLE_COLUMNS[4].label,
		cell: ({ row }) => (
			<div className="max-w-md space-y-1">
				{row.original.note ? (
					<>
						<div className="text-sm">{row.original.note}</div>
						{row.original.bankTransactionId && (
							<Badge variant="outline" className="text-xs">
								{row.original.bankTransactionId}
							</Badge>
						)}
					</>
				) : (
					<span className="text-sm text-muted-foreground">-</span>
				)}
			</div>
		),
	},
]

export function QuotaHistoryTable({ data, totalItems, isLoading }: Props) {
	return (
		<DataTablePaginate
			columns={columns}
			data={data}
			totalItems={totalItems}
			isLoading={isLoading}
		/>
	)
}
